// src/api/uninsuredApi.ts

import { serverApi, getAuthHeaders, handleReferenceResponse, queApi } from "@core/api/core"
import { handleApiResponse } from "@core/api/core/utils"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { CommonResponse, CommonTResponse, CommonReferenceResponse } from "@/types/common"

import {
  JaboClaimUninsuredItemResponse,
  JaboClaimUninsuredItemSerializer,
  UninsuredCategorySerializer,
  UninsuredCategoryResponse,
  UninsuredItemResponse,
  UninsuredItemSerializer,
} from "@/types/uninsured"

export interface InstalledResponse {
  message: string
  result: boolean
}
// import { arrangeErrors } from "@core/utils/errorUtil"

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 카테고리 목록조회
// ──────────────────────────────────────────────────────────────────────────────
export const getUninsuredCategories = async (): Promise<UninsuredCategorySerializer[]> => {
  const requestUrl = `/uninsured/category/`
  const response = await serverApi.get<UninsuredCategoryResponse>(requestUrl)
  return handleApiResponse<UninsuredCategorySerializer[]>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 신규저장
// ──────────────────────────────────────────────────────────────────────────────
export const createUninsuredCategory = async (payload: UninsuredCategorySerializer): Promise<boolean> => {
  const requestUrl = `/uninsured/category/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 카테고리 목록조회 (참조용)
// ──────────────────────────────────────────────────────────────────────────────
export const getReferenceUninsuredCategories = async (): Promise<UninsuredCategorySerializer[]> => {
  const requestUrl = `/reference/uninsured_category/`
  const response = await serverApi.get<CommonReferenceResponse>(requestUrl)

  const { columns, results } = handleReferenceResponse(response)

  const response_data: UninsuredCategorySerializer[] = results.map(row => {
    // columns[i] ↔ row[i] 매핑
    const obj = Object.fromEntries(columns.map((col, i) => [col, row[i]])) as Record<string, number | string | boolean>

    return {
      uninsuredCategoryUuid: String(obj["uninsuredCategoryUuid"]),
      uninsuredCategoryName: String(obj["uninsuredCategoryName"]),
      itemOrder: obj["itemOrder"] != null ? String(obj["itemOrder"]) : undefined,
      isActive: Boolean(obj["isActive"]),
    }
  })

  // console.log("response", response.data.result)
  return response_data as UninsuredCategorySerializer[]
}

// ──────────────────────────────────────────────────────────────────────────────
// 업데이트
export const updateUninsuredCategory = async (payload: UninsuredCategorySerializer): Promise<boolean> => {
  const requestUrl = `/uninsured/category/${payload.uninsuredCategoryUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

export const deleteUninsuredCategory = async (uninsuredCategoryUuid: string): Promise<boolean> => {
  const requestUrl = `/uninsured/category/${uninsuredCategoryUuid}/`
  const response = await serverApi.delete<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 상세목록
// ──────────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────────
// 목록 조회
export const getUninsuredItems = async (): Promise<UninsuredItemSerializer[]> => {
  const requestUrl = `/uninsured/item/`
  const response = await serverApi.get<UninsuredItemResponse>(requestUrl)

  return handleApiResponse<UninsuredItemSerializer[]>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 항목 목록조회 (reference 공개용)
// ──────────────────────────────────────────────────────────────────────────────
export const getReferenceUninsuredItems = async (): Promise<UninsuredItemSerializer[]> => {
  const requestUrl = `/reference/uninsured_item/`
  const response = await serverApi.get<CommonReferenceResponse>(requestUrl)

  const { columns, results } = handleReferenceResponse(response)

  return results.map(row => {
    const obj = Object.fromEntries(columns.map((col, i) => [col, row[i]])) as Record<string, number | string | boolean>

    return {
      uninsuredCategoryUuid: String(obj["uninsuredCategoryUuid"]),
      uninsuredCategoryName: String(obj["uninsuredCategoryName"]),
      uninsuredItemUuid: String(obj["uninsuredItemUuid"]),
      uninsuredItemCode: String(obj["uninsuredItemCode"]),
      uninsuredItemName: String(obj["uninsuredItemName"]),
      uninsuredItemPrice: Number(obj["uninsuredItemPrice"]),
      uninsuredItemDuration: obj["uninsuredItemDuration"] != null ? Number(obj["uninsuredItemDuration"]) : null,
      uninsuredItemIsTax: Boolean(obj["uninsuredItemIsTax"]),
      billSubclass: obj["billSubclass"] != null ? Number(obj["billSubclass"]) : undefined,
      itemOrder: obj["itemOrder"] != null ? Number(obj["itemOrder"]) : undefined,
      isActive: Boolean(obj["isActive"]),
    }
  }) as UninsuredItemSerializer[]
}

// ──────────────────────────────────────────────────────────────────────────────
// 신규저장
export const createUninsuredItem = async (payload: UninsuredItemSerializer): Promise<boolean> => {
  const requestUrl = `/uninsured/item/`
  const response = await serverApi.post<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 업데이트
export const updateUninsuredItem = async (payload: UninsuredItemSerializer): Promise<boolean> => {
  const requestUrl = `/uninsured/item/${payload.uninsuredItemUuid}/`
  const { billSubclass: _unusedBillSubclass, ...payloadWithoutBillSubclass } = payload as UninsuredItemSerializer & {
    billSubclass?: string
  }
  console.log("Updating uninsured item with payload:", payloadWithoutBillSubclass)
  const response = await serverApi.patch<CommonResponse>(requestUrl, payloadWithoutBillSubclass)

  console.log("Response from updating uninsured item:", response)
  return handleApiResponse<boolean>(response)
}

export const deleteUninsuredItem = async (uninsuredItemUuid: string): Promise<boolean> => {
  const requestUrl = `/uninsured/item/${uninsuredItemUuid}/`
  const response = await serverApi.delete<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

export const getJaboClaimUninsuredItems = async (): Promise<JaboClaimUninsuredItemSerializer[]> => {
  const requestUrl = `/uninsured/jabo-claim-item/`
  const response = await serverApi.get<JaboClaimUninsuredItemResponse>(requestUrl)
  const responseData = response.data
  if (Array.isArray(responseData?.results)) {
    return responseData.results
  }
  if (Array.isArray(responseData?.result)) {
    return responseData.result
  }
  return []
}

export const updateJaboClaimUninsuredItem = async (payload: JaboClaimUninsuredItemSerializer): Promise<boolean> => {
  const requestUrl = `/uninsured/jabo-claim-item/${payload.jaboClaimUninsuredItemUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 목록 조회 (참조용)
// ──────────────────────────────────────────────────────────────────────────────
export const fetchUninsuredCategory = createAsyncThunk<
  UninsuredCategorySerializer[], // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("uninsuredCategory/fetchUninsuredCategory", async (_, { rejectWithValue }) => {
  try {
    const requestUrl = `/reference/uninsured_category/`
    const response = await queApi.get<CommonReferenceResponse>(requestUrl)

    const { columns, results } = handleReferenceResponse(response)

    const datas: UninsuredCategorySerializer[] = results.map(row => {
      // columns[i] ↔ row[i] 매핑
      const obj = Object.fromEntries(columns.map((col, i) => [col, row[i]])) as Record<string, number | string | boolean>

      return {
        uninsuredCategoryUuid: String(obj["uninsuredCategoryUuid"]),
        uninsuredCategoryName: String(obj["uninsuredCategoryName"]),
        itemOrder: obj["itemOrder"] != null ? String(obj["itemOrder"]) : undefined,
        isActive: Boolean(obj["isActive"]),
      }
    })
    return datas
  } catch (err: any) {
    return rejectWithValue(err.message || "비급여 카테고리 조회 오류입니다.")
  }
})

// ──────────────────────────────────────────────────────────────────────────────
// 비급여 목록 조회 (참조용)
// ──────────────────────────────────────────────────────────────────────────────
export const fetchUninsuredItems = createAsyncThunk<
  UninsuredItemSerializer[], // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("uninsuredItem/fetchUninsuredItems", async (_, { rejectWithValue }) => {
  try {
    const requestUrl = `/reference/uninsured_item/`
    const response = await queApi.get<CommonReferenceResponse>(requestUrl)

    // console.debug("fetchVisitrouts 응답", response)
    const { columns, results } = handleReferenceResponse(response)

    const datas: UninsuredItemSerializer[] = results.map(row => {
      // columns[i] ↔ row[i] 매핑
      const obj = Object.fromEntries(columns.map((col, i) => [col, row[i]])) as Record<string, number | string | boolean>

      return {
        uninsuredCategoryUuid: String(obj["uninsuredCategoryUuid"]),
        uninsuredCategoryName: String(obj["uninsuredCategoryName"]),
        uninsuredItemUuid: String(obj["uninsuredItemUuid"]),
        uninsuredItemCode: String(obj["uninsuredItemCode"]),
        uninsuredItemName: String(obj["uninsuredItemName"]),
        uninsuredItemPrice: Number(obj["uninsuredItemPrice"]),
        uninsuredItemDuration: obj["uninsuredItemDuration"] != null ? Number(obj["uninsuredItemDuration"]) : null,
        uninsuredItemIsTax: Boolean(obj["uninsuredItemIsTax"]),
        billSubclass: obj["billSubclass"] != null ? Number(obj["billSubclass"]) : undefined,
        itemOrder: obj["itemOrder"] != null ? Number(obj["itemOrder"]) : undefined,
        isActive: Boolean(obj["isActive"]),
      }
    })
    // console.debug("fetchUninsuredItems 응답", datas)
    return datas
  } catch (err: any) {
    return rejectWithValue(err.message || "비급여 항목 조회 오류입니다.")
  }
})
